/**
 * Google Maps loader + location helpers.
 *
 * Env: REACT_APP_GOOGLE_MAPS_API_KEY
 *
 * If the key is not configured, consumers get a graceful fallback:
 *   - LocationPicker renders a text-only address input with manual lat/lng.
 *   - All helpers still resolve but without autocomplete / geocoding.
 */

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

let loadPromise = null;

/** Lazily injects the Google Maps Places script. Resolves to window.google. */
export const loadGoogleMaps = () => {
    if (!API_KEY) return Promise.resolve(null);
    if (typeof window === "undefined") return Promise.resolve(null);
    if (window.google?.maps?.places) return Promise.resolve(window.google);
    if (loadPromise) return loadPromise;

    loadPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-google-maps-loader="1"]');
        if (existing) {
            existing.addEventListener("load", () => resolve(window.google));
            existing.addEventListener("error", reject);
            return;
        }
        const s = document.createElement("script");
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(API_KEY)}&libraries=places`;
        s.async = true;
        s.defer = true;
        s.setAttribute("data-google-maps-loader", "1");
        s.onload = () => resolve(window.google);
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return loadPromise;
};

export const isGoogleMapsEnabled = () => !!API_KEY;

/** Reverse-geocode (lat,lng) → formatted address. Returns null if unavailable. */
export const reverseGeocode = async (lat, lng) => {
    if (!API_KEY) return null;
    try {
        const r = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${encodeURIComponent(API_KEY)}`
        );
        const data = await r.json();
        return data?.results?.[0]?.formatted_address || null;
    } catch { return null; }
};
